import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const upsertById = (list, item, prepend) => {
  if (list.some((x) => x.id === item.id)) return list.map((x) => (x.id === item.id ? item : x));
  return prepend ? [item, ...list] : [...list, item];
};

export function useCrmStore() {
  const [reps, setReps] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const leadsRef = useRef(leads);
  leadsRef.current = leads;

  const load = useCallback(async () => {
    try {
      const [r, l] = await Promise.all([api.get("/reps"), api.get("/leads")]);
      setReps(r.data);
      setLeads(l.data);
    } catch (e) {
      toast.error(errMsg(e, "Could not load data"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upsertLead = useCallback((lead) => setLeads((ls) => upsertById(ls, lead, true)), []);
  const removeLead = useCallback((id) => setLeads((ls) => ls.filter((x) => x.id !== id)), []);
  const upsertRep = useCallback((rep) => setReps((rs) => upsertById(rs, rep, false)), []);

  return { reps, leads, loading, load, leadsRef, setLeads, upsertLead, removeLead, upsertRep };
}

export function useLeadActions({ leadsRef, setLeads, upsertLead, setPendingLost }) {
  const commitMove = useCallback(async (id, status, lossReason) => {
    const prev = leadsRef.current.find((x) => x.id === id);
    if (!prev) return;
    setLeads((ls) => ls.map((x) => (x.id === id ? { ...x, status, loss_reason: lossReason || x.loss_reason } : x)));
    try {
      const { data } = await api.patch(`/leads/${id}/status`, { status, loss_reason: lossReason || null });
      upsertLead(data);
    } catch (e) {
      setLeads((ls) => ls.map((x) => (x.id === id ? prev : x)));
      toast.error(`${errMsg(e, "Move failed")}. Card returned to ${prev.status}.`);
    }
  }, [leadsRef, setLeads, upsertLead]);

  const moveLead = useCallback((id, status) => {
    const lead = leadsRef.current.find((x) => x.id === id);
    if (!lead || lead.status === status) return;
    if (status === "Lost") setPendingLost({ id, from: lead.status, name: lead.name });
    else commitMove(id, status);
  }, [leadsRef, commitMove, setPendingLost]);

  const touchLead = useCallback(async (id) => {
    try {
      const { data } = await api.post(`/leads/${id}/touch`);
      upsertLead(data);
      toast.success(`Touched ${data.name} today`);
      return data;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    }
  }, [upsertLead]);

  return { commitMove, moveLead, touchLead };
}
